import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import { SparkleIcon } from '@openx/components-core/icons/SparkleIcon/SparkleIcon.tsx';
import { useConfirmationDialog } from '@openx/components-core/lib/Dialog/ConfirmationDialog/useConfirmationDialog.tsx';
import { isInventoryFormatIncompatibleWithRbo } from '@openx/components-targeting/utils/isTargetingRboCompatible.ts';
import type { DealFormDataWithTargeting } from '@openx/types/deal.ts';
import { TargetingFields } from '@openx/types/targeting/targeting.ts';
import { useCachedFlag } from '@openx/utils/hooks/useCachedFlag.ts';
import { cleanUpNulls } from '@openx/utils/lib/cleanUpNulls.ts';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { RboFormatValidationContent } from '../components/RboFormatValidationContent.tsx';
import { RBO_FORMAT_VALIDATION_DONT_SHOW_FLAG } from '../constants.ts';

export const useRboFormatValidationPopup = () => {
  const { open, close } = useConfirmationDialog();
  const [dontShowAgain, setDontShowAgain] = useCachedFlag(RBO_FORMAT_VALIDATION_DONT_SHOW_FLAG, false);

  const { t } = useTranslation();

  const getActionsLeftContent = useCallback(
    () => (
      <FormControlLabel
        control={
          <Checkbox
            defaultChecked={false}
            onChange={e => {
              setDontShowAgain(e.target.checked);
            }}
            size="small"
            data-test="rbo-format-validation-dont-show-checkbox"
          />
        }
        label={t("Don't show this message again.")}
        sx={{ m: 0 }}
      />
    ),
    [t, setDontShowAgain]
  );

  const getTitleContent = useCallback(
    () => (
      <Stack
        direction="row"
        spacing={1}
        sx={{
          alignItems: 'center',
        }}
      >
        {t('Engagement Optimization')}
        <SparkleIcon />
      </Stack>
    ),
    [t]
  );

  const showRboFormatConfirmation = useCallback(
    (formValues: DealFormDataWithTargeting) =>
      new Promise<typeof formValues>((resolve, reject) => {
        const hasRboValues = !!cleanUpNulls(formValues.rbo_config);
        const inventoryFormat = formValues.targeting.targeting?.[TargetingFields.INVENTORY_FORMAT];

        if (
          !hasRboValues ||
          !isInventoryFormatIncompatibleWithRbo(inventoryFormat, {
            missingInventoryFormatIncompatible: true,
            missingAdPlacementIncompatible: true,
          })
        ) {
          return resolve(formValues);
        }

        const rboDisabledCopy: DealFormDataWithTargeting = { ...structuredClone(formValues), rbo_config: null };

        if (dontShowAgain) {
          return resolve(rboDisabledCopy);
        }

        open({
          content: <RboFormatValidationContent />,
          customContent: true,
          confirmationButtonColor: 'error',
          confirmationLabel: t('Disable Engagement Optimization'),
          cancelLabel: t('Cancel'),
          showExtraBtn: false,
          title: getTitleContent(),
          actionsLeftContent: getActionsLeftContent(),
          onCancel: () => {
            close();
            reject();
          },
          onConfirmed: () => {
            close();
            resolve(rboDisabledCopy);
          },
        });
      }),
    [dontShowAgain, open, close, getActionsLeftContent, getTitleContent, t]
  );

  return { showRboFormatConfirmation };
};
